import { FC, useEffect } from 'react'
import { useTable, useSortBy, useFilters } from 'react-table'
import { IProductDimension } from 'shared/models/IProductDimension'
import { ExportToCsv } from 'export-to-csv-fix-source-map'
import { exceloptions, WareHouseSearchOptions } from 'shared/models/Enum'
import SearchForm from './SearchForm'
import ActionButtons from './ActionButtons'
import Columns from './OverviewColumns'

interface IProps {
  productDimensions: IProductDimension[]
  warehouseId: number
  showDetails: (productDimension: IProductDimension) => void
  handleNew: () => void
}

const OverviewTable: FC<IProps> = ({
  productDimensions,
  warehouseId,
  showDetails,
  handleNew,
}) => {
  const { headerGroups, rows, getTableProps, prepareRow, setFilter } =
    useTable<IProductDimension>(
      {
        columns: Columns,
        data: productDimensions,
      },
      useFilters,
      useSortBy
    )

  useEffect(() => {
    handleWarehouseChange(warehouseId)
  }, [warehouseId])

  const handleExportToCsv = () => {
    if (rows.length === 0) {
      return
    }

    const csvExporter = new ExportToCsv(exceloptions)
    csvExporter.generateCsv(
      rows.map(row => {
        const pd = row.original
        return {
          Productgroepcode: pd.productGroupCode,
          Fustcode: pd.fustCode ?? '',
          Vestiging: WareHouseSearchOptions[pd.warehouseId],
          'Diepte in mm': pd.depthInMM ?? '',
          'Breedte in mm': pd.widthInMM ?? '',
          'Hoogte in mm': pd.heightInMM ?? '',
          'Diepte + %': pd.depthInPercentage ? `${pd.depthInPercentage}%` : '',
          'Breedte + %': pd.widthInPercentage ? `${pd.widthInPercentage}%` : '',
          'Hoogte + %': pd.heightInPercentage ? `${pd.heightInPercentage}%` : '',
        }
      })
    )
  }

  const handleProductGroupCodeChange = (productGroupCode: string) => {
    const value = productGroupCode.trim()
    setFilter('productGroupCode', value || undefined)
  }

  const handleFustCodeChange = (fustCode: string) => {
    const value = fustCode.trim()
    setFilter('fustCode', value || undefined)
  }

  const handleWarehouseChange = (warehouseId: number) => {
    setFilter('warehouseId', warehouseId)
  }

  return (
    <>
      <SearchForm
        warehouseId={warehouseId}
        handleFustCodeChange={handleFustCodeChange}
        handleProductGroupCodeChange={handleProductGroupCodeChange}
        handleWarehouseChange={handleWarehouseChange}
      />

      <table {...getTableProps()} className='selectie table-hover'>
        <tbody>
          <tr className='tablesubheader'>
            {headerGroups.map(headerGroup =>
              headerGroup.headers.map(
                ({
                  id,
                  isSorted,
                  isSortedDesc,
                  getHeaderProps,
                  getSortByToggleProps,
                  render,
                }) => {
                  return (
                    <th {...getHeaderProps(getSortByToggleProps())} key={id}>
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontWeight: isSorted ? 'bold' : 'normal',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <span>{render('Header')}</span>
                        {isSorted && <span>{isSortedDesc ? '↓' : '↑'}</span>}
                      </button>
                    </th>
                  )
                }
              )
            )}
          </tr>
          {rows.map(row => {
            prepareRow(row)
            return (
              <tr
                {...row.getRowProps()}
                key={row.original.productDimensionId}
                onClick={() => showDetails(row.original)}
              >
                {row.cells.map(cell => (
                  <td
                    {...cell.getCellProps()}
                    key={cell.column.id}
                    className='tddata'
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
      <ActionButtons handleNew={handleNew} handleExportToCsv={handleExportToCsv} />
    </>
  )
}

export default OverviewTable
